import React, { memo, useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ospThemes } from "@ospweb/sdk-ui";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { cn } from "utils";
import { AppImage } from "@components/NextImage";
import { DotsLoading } from "../../Loading";
import Translations from "@translate/components/Chat/Thinking/ThinkingHeader";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

interface ThinkingHeaderProps {
  isShowAnswer: boolean;
  titleClassName?: string;
  className?: string;
  contentClassName?: string;
  onClick?: () => void;
  chatId?: string;
  loading?: boolean;
  isLast?: boolean;
  agentKeys?: string[];
  isExpanded?: boolean;
  latestReasoningText?: string;
}

const globalTimers = new Map<string, NodeJS.Timeout>();
const globalUpdateFunctions = new Map<string, (time: number) => void>();

const ThinkingHeader: React.FC<ThinkingHeaderProps> = memo(
  ({
    isShowAnswer,
    className,
    contentClassName,
    titleClassName,
    onClick,
    chatId = "",
    loading,
    isLast,
    agentKeys = [],
    isExpanded = false,
    latestReasoningText,
  }) => {
    const [time, setTime] = useState(0);

    const timeStorageKey = useMemo(() => {
      return `thinking_time_${chatId}`;
    }, [chatId]);

    const timerKey = useMemo(() => `thinking_timer_${chatId}`, [chatId]);
    const isClick = typeof onClick === "function";
    const isThinking = loading && isLast && !isShowAnswer;
    const isFinished =
      (isShowAnswer && agentKeys?.length > 0) || !loading || !isLast;

    useEffect(() => {
      globalUpdateFunctions.set(timerKey, setTime);
      return () => {
        globalUpdateFunctions.delete(timerKey);
      };
    }, [timerKey]);

    useEffect(() => {
      const initTime = () => {
        const storeTime = localStorage.getItem(timeStorageKey);
        if (storeTime) {
          setTime(Number(storeTime));
        } else {
          setTime(0);
        }
      };
      initTime();
    }, [timeStorageKey]);

    useEffect(() => {
      if (globalTimers.has(timerKey)) {
        return;
      }

      if (loading && !isShowAnswer) {
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith("thinking_time_")) {
            localStorage.removeItem(key);
          }
        });

        const updateFn = globalUpdateFunctions.get(timerKey);
        if (updateFn) {
          updateFn(0);
        }

        let currentTime = 0;
        const timer = setInterval(() => {
          currentTime += 1;
          const updateFn = globalUpdateFunctions.get(timerKey);
          if (updateFn) {
            updateFn(currentTime);
          }
          localStorage.setItem(timeStorageKey, currentTime.toString());
        }, 1000);

        globalTimers.set(timerKey, timer);
      } else if (isShowAnswer) {
        const existingTimer = globalTimers.get(timerKey);
        if (existingTimer) {
          clearInterval(existingTimer);
          globalTimers.delete(timerKey);
        }
      }

      return () => {
        if (!isShowAnswer) {
          const existingTimer = globalTimers.get(timerKey);
          if (existingTimer) {
            clearInterval(existingTimer);
            globalTimers.delete(timerKey);
          }
        }
      };
    }, [loading, isShowAnswer, timeStorageKey, timerKey]);

    const thinkingIconSrc = isThinking
      ? "/assets/images/chat/thinking_indicator.webp"
      : "/assets/images/chat/thinking_indicator_static.png";

    return (
      <div
        className={cn(
          "flex items-center gap-2 cursor-pointer select-none",
          className,
        )}
        onClick={onClick}
        data-time={time}
      >
        <AppImage
          src={thinkingIconSrc}
          alt="thinking"
          width={24}
          height={24}
          className="flex-shrink-0"
        />

        {isThinking ? (
          <div className="flex items-center max-w-[65%]">
            <AnimatePresence mode="wait">
              <motion.div
                key={latestReasoningText || "loading"}
                initial={{ opacity: 0, x: 4 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -4 }}
                transition={{ duration: 0.2 }}
                className="body_m text-content_secondary text-gradient-scroll truncate"
              >
                {latestReasoningText || <DotsLoading />}
              </motion.div>
            </AnimatePresence>
            <OspIcons
              name="chevron-down"
              size={16}
              color={ospThemes.colors.content_secondary}
              className="flex-shrink-0 transition-transform duration-300"
              style={{
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </div>
        ) : isFinished ? (
          <div className={cn("flex items-center", contentClassName)}>
            <div
              className={cn(
                "body_output_m text-content_secondary",
                titleClassName,
              )}
            >
              {I18n.t("Thinking completed %{var0}", {
                var0: "",
              })}
            </div>
            {isClick && (
              <OspIcons
                name="chevron-down"
                size={16}
                color={ospThemes.colors.content_secondary}
                className="transition-transform duration-300"
                style={{
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            )}
          </div>
        ) : (
          <div className="body_medium_m flex items-center gap-1">
            <div className="w-[66px]">
              <DotsLoading />
            </div>
          </div>
        )}
      </div>
    );
  },
);

ThinkingHeader.displayName = "ThinkingHeader";

export default ThinkingHeader;
